import { LookingGlassConfig, LookingGlassWebXRPolyfill } from "@lookingglass/webxr";
import type { WebGLRenderer } from "three";
import { VISUAL } from "../config/visualConfig";

export class LookingGlassAdapter {
  static async initialize(): Promise<void> {
    // r180 detects this API when WebGLRenderer is constructed. Chrome's native
    // binding rejects polyfilled XRSession objects. Use the XRWebGLLayer path.
    Object.defineProperty(window, "XRWebGLBinding", { value: undefined, configurable: true, writable: true });
    await LookingGlassWebXRPolyfill.init({
      tileHeight: VISUAL.tileHeight,
      numViews: VISUAL.numViews,
      targetX: 0,
      targetY: 0,
      targetZ: 0,
      trackballY: VISUAL.cameraPitch,
      targetDiam: VISUAL.targetDiameter,
      fovy: (14 * Math.PI) / 180,
      depthiness: VISUAL.depthiness,
      inlineView: 1,
      filterMode: VISUAL.filterMode,
    });
  }

  static attach(renderer: WebGLRenderer, status: HTMLElement, onOutputReady: (popup: Window) => void): void {
    renderer.xr.enabled = true;
    const button = document.createElement("button");
    button.id = "VRButton";
    button.textContent = "ENTER LOOKING GLASS";
    // webxr 0.6.0 observes direct children of body when locating #VRButton.
    document.body.append(button);
    renderer.xr.addEventListener("sessionstart", () => {
      status.textContent = "XR開始 — 多視点フレームを確認中…";
      button.textContent = "EXIT LOOKING GLASS";
      document.body.classList.add("go-active");
    });
    renderer.xr.addEventListener("sessionend", () => {
      status.textContent = "XR停止 — EnterでGo画面を再表示";
      button.textContent = "ENTER LOOKING GLASS";
      document.body.classList.remove("go-active");
    });
    let starting = false;
    let ownedOutput: Window | null = null;
    window.addEventListener("pagehide", () => {
      // A reload destroys this renderer but leaves popup windows alive unless
      // we close them explicitly. Otherwise Go can show a stale, invalid frame.
      if (ownedOutput && !ownedOutput.closed) {
        ownedOutput.onbeforeunload = null;
        ownedOutput.close();
      }
    });
    const openOutput = async (): Promise<void> => {
      if (starting) return;
      const config = LookingGlassConfig;
      // The SDK falls back to a landscape, single-view display when Bridge
      // cannot provide calibration. That is not a valid Go stereo output.
      if (!config.calibration.serial || config.numViews < 2) {
        status.textContent = "Goの校正が未取得です — Bridgeを再起動し、ページを再読み込みしてください";
        document.querySelector<HTMLDetailsElement>("#connection-details")!.open = true;
        return;
      }
      // Reserve the output synchronously in the click, before requestSession.
      // The polyfill otherwise awaits screen placement and loses activation.
      let popup = config.popup && !config.popup.closed
        ? config.popup
        : window.open("", "dual-stack-output", "width=540,height=800");
      if (!popup) {
        status.textContent = "ポップアップがブロックされています — Chromeの許可後に再度開いてください";
        return;
      }
      ownedOutput = popup;
      popup.document.title = "DualStack — Go出力（Goへ移動してダブルクリック）";
      popup.document.body.style.cssText = "margin:0;background:black;overflow:hidden;";
      popup.focus();
      starting = true;
      try {
        if (!renderer.xr.isPresenting) {
          if (!navigator.xr) throw new Error("WebXRが利用できません");
          // Let the SDK initialize its controls and teardown state. Supplying
          // config.popup before this skips that state and makes session.end fail.
          config.popup = null;
          status.textContent = "Go出力ウィンドウを開きました — 立体視を開始中…";
          const session = await navigator.xr.requestSession("immersive-vr", { optionalFeatures: ["local-floor"] });
          if (popup.closed) { await session.end(); config.popup = null; return; }
          await renderer.xr.setSession(session);
          // updateRenderState applies the base layer at the first XR frame,
          // after setSession resolves. Keep popup unset until SDK setup runs.
          await new Promise<void>(resolve => session.requestAnimationFrame(() => resolve()));
          const placed = config.popup as Window | null;
          if (placed && !placed.closed && placed !== popup) {
            popup.close();
            popup = placed;
            ownedOutput = placed;
          }
        }
        const canvas = config.lkgCanvas;
        if (!canvas) throw new Error("出力キャンバスが作成されませんでした");
        config.popup = popup;
        popup.document.title = "DualStack — Go出力（Goへ移動してダブルクリック）";
        popup.document.body.style.cssText = "margin:0;background:black;overflow:hidden;";
        canvas.width = config.calibration.screenW.value;
        canvas.height = config.calibration.screenH.value;
        canvas.style.cssText = "position:fixed;top:0;left:0;";
        popup.document.body.replaceChildren(canvas);
        popup.onbeforeunload = () => {
          const session = renderer.xr.getSession();
          if (session && renderer.xr.isPresenting) void session.end();
        };
        onOutputReady(popup);
        canvas.focus();
        popup.focus();
      } catch (error) {
        console.error("Looking Glass output failed", error);
        status.textContent = `立体視の開始に失敗: ${error instanceof Error ? error.message : String(error)}`;
        if (!renderer.xr.isPresenting) {
          popup.onbeforeunload = null;
          popup.close();
          config.popup = null;
        }
      } finally {
        starting = false;
      }
    };
    button.addEventListener("click", () => {
      if (renderer.xr.isPresenting) void renderer.xr.getSession()?.end();
      else void openOutput();
    });
    document.querySelector<HTMLButtonElement>("#open-output")!.addEventListener("click", () => { void openOutput(); });
    window.addEventListener("keydown", event => {
      if (event.code !== "Enter" || event.repeat || (event.target as HTMLElement)?.closest("input, button, select, textarea, summary")) return;
      event.preventDefault();
      void openOutput();
    });
  }

  static get config() { return LookingGlassConfig; }
}
